import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/document-intel';
const AUDIO_VERSION = 'v5';

/** Hardcoded demo file representing a sample scanned invoice PDF */
const DEMO_FILE = {
  fileName: 'demo_invoice.pdf',
  fileSize: 2800000,
  uploadDate: '2026-02-23T10:00:00Z',
  s3Key: 'users/demo/demo_invoice.pdf',
  contentType: 'application/pdf',
};

/** Mock schema for OCR-extracted document data */
const DEMO_SCHEMA = [
  { name: 'doc_id', data_type: 'INT64' },
  { name: 'filename', data_type: 'BYTE_ARRAY' },
  { name: 'page_number', data_type: 'INT64' },
  { name: 'extracted_text', data_type: 'BYTE_ARRAY' },
  { name: 'doc_type', data_type: 'BYTE_ARRAY' },
  { name: 'date', data_type: 'BYTE_ARRAY' },
  { name: 'amount', data_type: 'BYTE_ARRAY' },
  { name: 'vendor', data_type: 'BYTE_ARRAY' },
];

/** Mock rows of OCR-extracted document data */
const DEMO_ROWS = [
  { doc_id: 2001, filename: 'INV-2026-0451.pdf', page_number: 1, extracted_text: 'Invoice #0451. Bill To: Acme Corp. Payment terms: Net 30. Total due: $12,450.00. Please remit payment by March 15, 2026.', doc_type: 'Invoice', date: '2026-02-14', amount: '$12,450.00', vendor: 'TechSupply Inc.' },
  { doc_id: 2002, filename: 'Contract-ServiceAgreement.pdf', page_number: 1, extracted_text: 'Master Service Agreement between DataFlow LLC and Acme Corp. Effective date: January 1, 2026. Term: 24 months. Monthly retainer: $8,500.00.', doc_type: 'Contract', date: '2026-01-01', amount: '$8,500.00', vendor: 'DataFlow LLC' },
  { doc_id: 2003, filename: 'Receipt-AWS-Feb2026.pdf', page_number: 1, extracted_text: 'AWS billing receipt for February 2026. Account ID: 9408-4701-7865. Services: EC2, S3, Lambda, CloudFront. Total charges: $3,217.89.', doc_type: 'Receipt', date: '2026-02-01', amount: '$3,217.89', vendor: 'Amazon Web Services' },
  { doc_id: 2004, filename: 'INV-2026-0467.pdf', page_number: 1, extracted_text: 'Invoice #0467. Consulting services rendered January 15–February 14. Payment terms: Net 15. Amount due: $6,750.00. Late fee: 1.5% per month.', doc_type: 'Invoice', date: '2026-02-18', amount: '$6,750.00', vendor: 'Pinnacle Consulting' },
  { doc_id: 2005, filename: 'PO-2026-1138.pdf', page_number: 1, extracted_text: 'Purchase Order #1138. Ship to: Warehouse B, 450 Industrial Pkwy. 500x USB-C cables @ $4.20 ea. Total: $2,100.00. Delivery by March 1, 2026.', doc_type: 'Purchase Order', date: '2026-02-10', amount: '$2,100.00', vendor: 'GlobalParts Ltd.' },
  { doc_id: 2006, filename: 'NDA-BigSplice-2026.pdf', page_number: 1, extracted_text: 'Non-Disclosure Agreement. Parties: BigSplice Inc. and Acme Corp. Confidentiality period: 3 years from effective date. Governing law: State of Delaware.', doc_type: 'NDA', date: '2026-02-05', amount: 'N/A', vendor: 'BigSplice Inc.' },
];

/** Mock search results for "payment terms" query */
const DEMO_SEARCH_RESULTS = [
  { doc_id: 2001, filename: 'INV-2026-0451.pdf', extracted_text: 'Invoice #0451. Payment terms: Net 30. Total due: $12,450.00...', doc_type: 'Invoice', vendor: 'TechSupply Inc.' },
  { doc_id: 2004, filename: 'INV-2026-0467.pdf', extracted_text: 'Payment terms: Net 15. Amount due: $6,750.00. Late fee: 1.5% per month...', doc_type: 'Invoice', vendor: 'Pinnacle Consulting' },
  { doc_id: 2002, filename: 'Contract-ServiceAgreement.pdf', extracted_text: 'Payment terms outlined in Exhibit A. Monthly retainer: $8,500.00...', doc_type: 'Contract', vendor: 'DataFlow LLC' },
];

/**
 * Document Intelligence demo script with LIVE DATA injection.
 * Uses hardcoded demo_invoice.pdf with mock OCR-extracted document data
 * flowing through all 5 nodes of the document-intel flow template.
 */
export const documentIntelScript: DemoScript = {
  id: 'document-intel',
  templateId: 'document-intel',
  name: 'Document Intelligence Demo',
  description: 'Learn how to OCR scanned documents, browse extracted text, and search across your documents.',
  steps: [
    // === INTRO (no target) ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Let me walk you through our document intelligence pipeline \u2014 I\u2019ll show you how to go from scanned PDFs to fully searchable, structured data.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 8500 },
    },

    // === STEP 1: Upload Document ===
    {
      id: 'upload-highlight',
      type: 'highlight',
      target: { nodeId: 'upload' },
      delayBefore: 200,
      subtitle: 'Start here by uploading your documents. We support PDFs, scanned images, and multi-page contracts \u2014 drop them right in.',
      audio: { src: `${AUDIO_BASE}/upload-highlight.mp3?${AUDIO_VERSION}`, duration: 7500 },
      cursorPath: [
        { x: -80, y: -40, duration: 600 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'upload-sample-note',
      type: 'narrate',
      target: { nodeId: 'upload' },
      subtitle: 'Just so you know, this demo uses simulated documents to walk you through each step quickly. Everything you see here works the same way with your own PDFs \u2014 you can try it right after.',
      audio: { src: `${AUDIO_BASE}/upload-sample-note.mp3?${AUDIO_VERSION}`, duration: 10000 },
    },
    {
      id: 'upload-decision',
      type: 'decision',
      target: { nodeId: 'upload' },
      subtitle: 'Want me to use a sample invoice, or would you like to upload your own document?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 3500 },
      decision: {
        prompt: 'Want me to use a sample invoice, or would you like to upload your own document?',
        options: [
          { label: 'Use sample invoice', icon: 'file-text', nextStepId: 'upload-inject-uploading' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { nodeId: 'upload' },
      subtitle: 'Go ahead and drag a PDF onto the upload area. I\u2019ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 4000 },
      autoAdvanceAfter: 4000,
    },

    // === INJECT: Simulate file upload progress ===
    {
      id: 'upload-inject-uploading',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Uploading document\u2026',
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_invoice.pdf', progress: 35 } },
      },
      autoAdvanceAfter: 250,
    },
    {
      id: 'upload-inject-progress',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_invoice.pdf', progress: 72 } },
      },
      autoAdvanceAfter: 200,
    },
    {
      id: 'upload-inject-almost',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'demo_invoice.pdf', progress: 95 } },
      },
      autoAdvanceAfter: 150,
    },
    {
      id: 'upload-inject-done',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Upload complete.',
      audio: { src: `${AUDIO_BASE}/upload-inject.mp3?${AUDIO_VERSION}`, duration: 1500 },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'done', fileName: 'demo_invoice.pdf' } },
      },
    },

    // === STEP 2: OCR Processing (with live status animation) ===
    {
      id: 'ocr-inject-processing',
      type: 'inject',
      target: { nodeId: 'auto-process' },
      delayBefore: 150,
      subtitle: 'BigSplice automatically runs OCR on your documents using state-of-the-art vision models. Tables, handwriting, multi-column layouts \u2014 it handles them all, extracting clean structured text from every page.',
      audio: { src: `${AUDIO_BASE}/ocr-highlight.mp3?${AUDIO_VERSION}`, duration: 14000 },
      inject: {
        nodeId: 'auto-process',
        data: { demoState: { status: 'processing' } },
      },
      cursorPath: [
        { x: -60, y: 0, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    {
      id: 'ocr-inject-complete',
      type: 'inject',
      target: { nodeId: 'auto-process' },
      subtitle: 'OCR complete. Your documents have been converted to structured data.',
      audio: { src: `${AUDIO_BASE}/ocr-wait.mp3?${AUDIO_VERSION}`, duration: 5000 },
      inject: {
        nodeId: 'auto-process',
        data: { demoState: { status: 'complete' } },
      },
    },

    // === STEP 3: View Data ===
    {
      id: 'dataset-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      subtitle: 'Now let\u2019s view the processed data and explore what was extracted.',
      audio: { src: `${AUDIO_BASE}/dataset-highlight.mp3?${AUDIO_VERSION}`, duration: 3500 },
      cursorPath: [
        { x: -40, y: -30, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    // Pre-inject mock data into downstream nodes BEFORE file selection triggers them
    {
      id: 'sheets-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'sheets',
        data: {
          demoState: {
            schema: DEMO_SCHEMA,
            rows: DEMO_ROWS,
            rowCount: 1243,
          },
        },
      },
      autoAdvanceAfter: 100,
    },
    {
      id: 'search-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'search',
        data: {
          demoState: {},
        },
      },
      autoAdvanceAfter: 100,
    },
    // Now inject the file \u2014 downstream nodes already have demoState and won\u2019t call APIs
    {
      id: 'dataset-inject',
      type: 'inject',
      target: { nodeId: 'sheets' },
      subtitle: 'Loading data\u2026 Watch how the extracted data flows through the pipeline.',
      audio: { src: `${AUDIO_BASE}/dataset-selected.mp3?${AUDIO_VERSION}`, duration: 8000 },
      inject: {
        nodeId: 'sheets',
        data: { selectedFile: DEMO_FILE },
      },
    },

    // === STEP 4: Browse Data (mock data already loaded via demoState) ===
    {
      id: 'sheets-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 400,
      subtitle: 'Here\u2019s your extracted data in spreadsheet form. Each row is a page from your documents \u2014 you can see the filename, extracted text, document type, amounts, and more.',
      audio: { src: `${AUDIO_BASE}/sheets-highlight.mp3?${AUDIO_VERSION}`, duration: 10000 },
      cursorPath: [
        { x: -100, y: -60, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 50, y: 30, duration: 400 },
      ],
    },
    {
      id: 'sheets-decision',
      type: 'decision',
      target: { nodeId: 'sheets' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the extracted data?',
        options: [
          { label: 'Explore the data', icon: 'search', nextStepId: 'sheets-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },
    {
      id: 'sheets-interact',
      type: 'interact',
      target: { nodeId: 'sheets' },
      subtitle: 'Take a moment to browse the extracted text. Click any column header to sort, or scroll through the documents.',
      audio: { src: `${AUDIO_BASE}/sheets-interact.mp3?${AUDIO_VERSION}`, duration: 6500 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 5: Full-Text Search ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { nodeId: 'search' },
      subtitle: 'Full-text search across all your extracted documents. Find specific clauses, invoice numbers, or any keyword in seconds.',
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 8500 },
      cursorPath: [
        { x: -60, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'search-decision',
      type: 'decision',
      target: { nodeId: 'search' },
      subtitle: '',
      decision: {
        prompt: 'Want to try a search?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-interact' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'search-demo',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Let me search for payment terms across all documents \u2014 watch this.',
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 4000 },
      interaction: { action: 'type', delay: 100, value: 'payment terms' },
      autoAdvanceAfter: 1200,
    },
    // Inject mock search results after typing
    {
      id: 'search-inject-results',
      type: 'inject',
      target: { nodeId: 'search' },
      inject: {
        nodeId: 'search',
        data: {
          demoState: {
            results: DEMO_SEARCH_RESULTS,
            resultCount: 3,
          },
        },
      },
      autoAdvanceAfter: 1200,
    },
    {
      id: 'search-interact',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Type any search term into the search box. Try phrases like "net 30" or "confidentiality".',
      audio: { src: `${AUDIO_BASE}/search-interact.mp3?${AUDIO_VERSION}`, duration: 8500 },
      autoAdvanceAfter: 4000,
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\u2019s the complete pipeline \u2014 from scanned documents to searchable, structured data. Ready to process your own documents?',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 7500 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Start my own flow', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
